import React, { useCallback, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Edit, Eye, Save, X, RotateCcw, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '../../services/api';
const API = process.env.REACT_APP_BACKEND_URL;
const MIN_CHARS = 50;

/**
 * Editier-Reihe für ein einzelnes Rechtsdokument (Impressum / AGB / Datenschutz).
 *
 * Defensive Implementation:
 * - State-Updates sind in try/catch verpackt (kein UI-Crash bei großen Pastes)
 * - Vorschau-Tab wird nur gerendert wenn sie aktiv ist (verhindert kostspieliges
 *   dangerouslySetInnerHTML-Rendering bei jedem Keystroke)
 * - Textarea hat eine harte Höhenbegrenzung + interne Scrollbar
 * - Reset/Save laufen über defensive Wrapper – ein Server-500er stürzt den
 *   Editor nicht ab, sondern zeigt nur einen Toast.
 *
 * Props:
 * - type:        'impressum' | 'agb' | 'datenschutz'
 * - title:       Sichtbarer Name
 * - subtitle:    Kurze Beschreibung
 * - page:        Backend-Objekt aus /api/legal
 * - onSaved:     Callback nach erfolgreichem Speichern
 */
const LegalDocumentRow = ({ type, title, subtitle, page, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');

  const safePage = page || {};
  const isEmpty = !!safePage.isEmpty;
  const lastUpdated = safePage.lastUpdated;
  const lastEditedBy = safePage.lastEditedBy;
  const fallbackContent = safePage.content || '';
  const rawContent = safePage.rawContent || '';

  const openEditor = useCallback(() => {
    const initial = rawContent.trim() ? rawContent : fallbackContent;
    setContent(initial);
    setOpen(true);
    setActiveTab('edit');
  }, [rawContent, fallbackContent]);

  const handleContentChange = useCallback((next) => {
    // Defensive: Setze immer einen String, niemals undefined/null
    try {
      setContent(typeof next === 'string' ? next : String(next ?? ''));
    } catch (e) {
      // Würde nur passieren, wenn React/Setter explodiert – wir vermeiden Crash
      console.error('LegalDocumentRow setContent failed:', e);
    }
  }, []);

  const dirty = content !== rawContent && content !== fallbackContent;

  const handleClose = useCallback((force = false) => {
    if (!force && dirty && content.trim().length > 0) {
      const ok = window.confirm('Änderungen verwerfen?');
      if (!ok) return;
    }
    setOpen(false);
  }, [dirty, content]);

  const handleSave = useCallback(async () => {
    const trimmedLen = content.trim().length;
    if (trimmedLen < MIN_CHARS) {
      toast.error(`Inhalt ist zu kurz (mindestens ${MIN_CHARS} Zeichen).`);
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(`${API}/api/legal/${type}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify({ content }) });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      if (!res.ok || !data || data.success !== true) {
        const msg = (data && data.detail) || `Speichern fehlgeschlagen (HTTP ${res.status})`;
        throw new Error(msg);
      }
      toast.success(data.message || `${title} gespeichert`, {
        description: `${data.length || content.length} Zeichen gespeichert` });
      setOpen(false);
      try { onSaved?.(); } catch (err) {
        // Elternhandler-Fehler dürfen den Save-Dialog-Close nicht blockieren
        console.warn('LegalDocumentRow: onSaved-Callback warf', err);
      }
    } catch (err) {
      toast.error(err?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }, [content, type, title, onSaved]);

  const handleResetToDefault = useCallback(async () => {
    const ok = window.confirm(
      `${title} auf die Original-Vorlage zurücksetzen?\n\nDie aktuelle Eingabe im Editor wird ersetzt. Erst beim Klick auf "Speichern" wird die Änderung dauerhaft gespeichert.`,
    );
    if (!ok) return;
    setResetting(true);
    try {
      const res = await apiFetch(`${API}/api/legal/${type}/default-template`, {
        headers: { } });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      if (!res.ok || !data || data.success !== true) {
        // Fallback: lokales fallbackContent benutzen (das Backend hatte es ohnehin geliefert)
        if (fallbackContent && fallbackContent.length > MIN_CHARS) {
          handleContentChange(fallbackContent);
          toast.success('Vorlage geladen (offline)', {
            description: 'Bitte abschließend auf "Speichern" klicken.' });
          return;
        }
        throw new Error((data && data.detail) || 'Vorlage konnte nicht geladen werden');
      }
      handleContentChange(data.content || '');
      toast.success('Vorlage geladen', {
        description: 'Bitte abschließend auf "Speichern" klicken, um sie zu übernehmen.' });
    } catch (err) {
      toast.error(err?.message || 'Fehler beim Laden der Vorlage');
    } finally {
      setResetting(false);
    }
  }, [type, title, fallbackContent, handleContentChange]);

  const trimmedLen = content.trim().length;
  const tooShort = trimmedLen < MIN_CHARS;

  return (
    <div
      className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50"
      data-testid={`legal-row-${type}`}
    >
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold">{title}</h3>
            {isEmpty && (
              <span
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/40"
                data-testid={`legal-empty-warn-${type}`}
              >
                <AlertTriangle className="h-3 w-3" /> Leer – aktuell wird die Vorlage angezeigt
              </span>
            )}
            {!isEmpty && safePage.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                <CheckCircle2 className="h-3 w-3" /> Aktiv
              </span>
            )}
          </div>
          <p className="text-gray-400 text-sm">{subtitle}</p>
          {lastUpdated && (
            <p className="text-gray-500 text-xs mt-1">
              Zuletzt bearbeitet: {new Date(lastUpdated).toLocaleString('de-DE')}
              {lastEditedBy && <> · von {lastEditedBy}</>}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
            onClick={() => window.open(`/legal/${type}`, '_blank', 'noopener,noreferrer')}
            data-testid={`legal-view-${type}`}
          >
            <Eye className="h-4 w-4 mr-1" /> Ansehen
          </Button>
          <Button
            size="sm"
            className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold"
            onClick={openEditor}
            data-testid={`legal-edit-${type}`}
          >
            <Edit className="h-4 w-4 mr-1" /> Bearbeiten
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent
          className="bg-gray-900 border-yellow-600/30 max-w-5xl w-full max-h-[92vh] overflow-y-auto"
          data-testid={`legal-dialog-${type}`}
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-yellow-500" />
              {title} bearbeiten
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              HTML wird unterstützt. Die öffentliche Seite <code className="text-yellow-400">/legal/{type}</code> wird sofort nach dem Speichern aktualisiert.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="bg-gray-800 border border-gray-700">
              <TabsTrigger value="edit" data-testid={`legal-tab-edit-${type}`}>
                <Edit className="h-4 w-4 mr-2" /> Editor
              </TabsTrigger>
              <TabsTrigger value="preview" data-testid={`legal-tab-preview-${type}`}>
                <Eye className="h-4 w-4 mr-2" /> Vorschau
              </TabsTrigger>
            </TabsList>

            <TabsContent value="edit" className="mt-3 space-y-3" forceMount>
              <div style={{ display: activeTab === 'edit' ? 'block' : 'none' }}>
                <Textarea
                  value={content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  onPaste={(e) => {
                    // Defensiver Paste-Handler: nutzt den Default, aber loggt Fehler
                    try { /* default paste */ } catch (err) { console.error(err); }
                  }}
                  className="bg-gray-800 border-yellow-600/20 text-white font-mono text-sm min-h-[55vh] max-h-[55vh] resize-none overflow-y-auto"
                  rows={20}
                  placeholder="<h1>Überschrift</h1>&#10;<p>Inhalt...</p>"
                  spellCheck={false}
                  data-testid={`legal-textarea-${type}`}
                />
                <div className="flex justify-between items-center text-xs mt-2">
                  <span className={tooShort ? 'text-red-400' : 'text-gray-400'}>
                    {content.length} Zeichen{tooShort && ` (mindestens ${MIN_CHARS} erforderlich)`}
                  </span>
                  <button
                    type="button"
                    onClick={handleResetToDefault}
                    disabled={resetting}
                    className="text-yellow-500 hover:text-yellow-400 underline-offset-2 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`legal-reset-${type}`}
                  >
                    <RotateCcw className={`h-3 w-3 ${resetting ? 'animate-spin' : ''}`} />
                    {resetting ? 'Lade…' : 'Auf Original-Vorlage zurücksetzen'}
                  </button>
                </div>
              </div>
            </TabsContent>

            {/* Vorschau wird NUR gerendert wenn aktiv – schont React beim Tippen */}
            {activeTab === 'preview' && (
              <TabsContent value="preview" className="mt-3" forceMount>
                <div
                  className="prose prose-invert max-w-none bg-black/40 border border-gray-700 rounded-lg p-6 min-h-[55vh] max-h-[55vh] overflow-auto
                    prose-headings:text-yellow-500
                    prose-h1:text-3xl prose-h1:font-bold prose-h1:mb-6
                    prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-yellow-400
                    prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3 prose-h3:text-yellow-300
                    prose-p:text-gray-200 prose-p:leading-relaxed
                    prose-a:text-yellow-500 hover:prose-a:text-yellow-400
                    prose-strong:text-white"
                  data-testid={`legal-preview-${type}`}
                  dangerouslySetInnerHTML={{ __html: content || '<p class="text-gray-500">Keine Vorschau – Editor ist leer.</p>' }}
                />
              </TabsContent>
            )}
          </Tabs>

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-800">
            <Button
              variant="outline"
              onClick={() => handleClose()}
              disabled={saving}
              className="border-gray-600 text-gray-200 hover:bg-gray-800"
              data-testid={`legal-cancel-${type}`}
            >
              <X className="h-4 w-4 mr-2" /> Abbrechen
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || tooShort}
              className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400 disabled:opacity-50"
              data-testid={`legal-save-${type}`}
            >
              <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
              {saving ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LegalDocumentRow;
